/**
 * Geometry and layout for the money-flow map.
 *
 * Kept separate from rendering so the maths is unit-testable without a canvas.
 *
 * Coordinate spaces:
 *   world  - where nodes live; stable, independent of pan/zoom
 *   screen - canvas pixels; world transformed by the viewport
 *
 * All hit-testing converts screen -> world so that dragging and zooming stay
 * correct at any scale, which the previous fixed-pixel layout could not do.
 */

import type { FlowNode, FlowAccountType, FlowPeriod, FlowEdge } from '../../services/flow-service';

export interface Viewport {
  x: number;
  y: number;
  /** Zoom scale. */
  k: number;
}

export interface Point {
  x: number;
  y: number;
}

export const MIN_ZOOM = 0.25;
export const MAX_ZOOM = 4;

/** world -> screen */
export const toScreen = (p: Point, v: Viewport): Point => ({
  x: p.x * v.k + v.x,
  y: p.y * v.k + v.y,
});

/** screen -> world */
export const toWorld = (p: Point, v: Viewport): Point => ({
  x: (p.x - v.x) / v.k,
  y: (p.y - v.y) / v.k,
});

/**
 * Zoom about a fixed screen point (the cursor), so the world point under the
 * pointer stays put. Zooming about the origin instead makes the graph appear
 * to slide away, which reads as broken.
 */
export const zoomAt = (v: Viewport, screenPoint: Point, factor: number): Viewport => {
  const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.k * factor));
  // Solve so toWorld(screenPoint) is identical before and after.
  const wx = (screenPoint.x - v.x) / v.k;
  const wy = (screenPoint.y - v.y) / v.k;
  return { k, x: screenPoint.x - wx * k, y: screenPoint.y - wy * k };
};

/** Column order, left to right: money's actual direction through the household. */
const COLUMN_ORDER: FlowAccountType[][] = [
  ['Income'],
  ['Asset'],
  ['Debt', 'Investment'],
  ['Expense'],
];

/** Equity is bookkeeping-only and would add a column of noise; park it with Income. */
const columnFor = (type: FlowAccountType): number => {
  const i = COLUMN_ORDER.findIndex((c) => c.includes(type));
  return i === -1 ? 0 : i;
};

export interface LayoutOptions {
  width: number;
  height: number;
  /** Positions the user has dragged; these win over the computed layout. */
  overrides?: Record<string, Point>;
}

/**
 * Compute world positions for nodes in column layout.
 *
 * Args:
 *   nodes: Nodes to place.
 *   options: Canvas size and any user-dragged overrides.
 *
 * Returns:
 *   Record<accountId, Point>: World position per node.
 */
export function layoutColumns(
  nodes: FlowNode[],
  { width, height, overrides = {} }: LayoutOptions,
): Record<string, Point> {
  const padX = 140;
  const usableW = Math.max(1, width - padX * 2);
  const columns: FlowNode[][] = COLUMN_ORDER.map(() => []);

  for (const n of nodes) {
    columns[columnFor(n.account_type)].push(n);
  }

  const out: Record<string, Point> = {};
  columns.forEach((col, ci) => {
    // Biggest movers at the top: the eye lands on them first.
    const sorted = [...col].sort((a, b) => Number(b.volume) - Number(a.volume));
    const x = padX + (usableW * ci) / Math.max(1, COLUMN_ORDER.length - 1);
    const gap = height / (sorted.length + 1);
    sorted.forEach((n, i) => {
      out[n.account_id] = overrides[n.account_id] ?? { x, y: gap * (i + 1) };
    });
  });

  return out;
}

/**
 * Node radius from volume.
 *
 * sqrt so AREA is proportional to volume; using the radius directly would make
 * a 4x larger account look 16x larger and badly overstate it.
 */
export const nodeRadius = (volume: number, maxVolume: number): number => {
  if (maxVolume <= 0) return 7;
  return 7 + Math.sqrt(Math.max(0, volume) / maxVolume) * 30;
};

/** Edge stroke width from value, clamped so one huge edge cannot dominate. */
export const edgeWidth = (value: number, maxValue: number): number => {
  if (maxValue <= 0) return 1;
  return 0.8 + Math.min(1, value / maxValue) * 7;
};

/** Default colour per account type, as a CSS custom property name. */
export const typeColorVar = (type: FlowAccountType): string => {
  switch (type) {
    case 'Income': return '--inflow';
    case 'Asset': return '--inflow';
    case 'Debt': return '--outflow';
    case 'Investment': return '--invest';
    case 'Expense': return '--outflow';
    default: return '--text-faint';
  }
};

/**
 * Fit all nodes into the viewport with margin.
 *
 * Returns a viewport rather than mutating, so callers can animate toward it.
 */
export function fitToNodes(
  positions: Record<string, Point>,
  width: number,
  height: number,
  margin = 90,
): Viewport {
  const pts = Object.values(positions);
  if (!pts.length) return { x: 0, y: 0, k: 1 };

  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);

  const w = Math.max(1, maxX - minX);
  const h = Math.max(1, maxY - minY);
  const k = Math.min(
    MAX_ZOOM,
    Math.max(MIN_ZOOM, Math.min((width - margin * 2) / w, (height - margin * 2) / h)),
  );

  return {
    k,
    x: width / 2 - ((minX + maxX) / 2) * k,
    y: height / 2 - ((minY + maxY) / 2) * k,
  };
}

/**
 * Point along the cubic bezier used for edges, at parameter t.
 *
 * Must stay in sync with the path the renderer strokes, or particles will
 * visibly drift off their own edge.
 */
export const bezierPoint = (a: Point, b: Point, t: number): Point => {
  const mx = (a.x + b.x) / 2;
  const u = 1 - t;
  return {
    x: u * u * u * a.x + 3 * u * u * t * mx + 3 * u * t * t * mx + t * t * t * b.x,
    y: u * u * u * a.y + 3 * u * u * t * a.y + 3 * u * t * t * b.y + t * t * t * b.y,
  };
};

// ---------------------------------------------------------------------------
// Timeline interpolation
// ---------------------------------------------------------------------------


const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/**
 * Blend two adjacent months into a synthetic in-between frame.
 *
 * Scrubbing that snaps from month to month reads as a slideshow. Interpolating
 * node volumes and edge values makes the graph appear to breathe as the
 * timeline moves, which is the difference between "stepping through data" and
 * "watching the year happen".
 *
 * Nodes and edges that exist in only one of the two months fade in or out
 * rather than popping, by treating the missing side as zero.
 *
 * Args:
 *   a: Earlier period.
 *   b: Later period (may be the same object at the timeline's end).
 *   t: Blend factor in [0, 1]; 0 yields `a`, 1 yields `b`.
 *
 * Returns:
 *   FlowPeriod: A synthetic period safe to render but not to persist.
 */
export function interpolatePeriods(a: FlowPeriod, b: FlowPeriod, t: number): FlowPeriod {
  if (t <= 0) return a;
  if (t >= 1) return b;

  const byIdA = new Map(a.nodes.map((n) => [n.account_id, n]));
  const byIdB = new Map(b.nodes.map((n) => [n.account_id, n]));
  const nodeIds = new Set([...byIdA.keys(), ...byIdB.keys()]);

  const nodes = [...nodeIds].map((id) => {
    const na = byIdA.get(id);
    const nb = byIdB.get(id);
    // Whichever side exists supplies the identity fields; only the magnitudes
    // are blended.
    const base = na ?? nb!;
    return {
      ...base,
      inflow: String(lerp(Number(na?.inflow ?? 0), Number(nb?.inflow ?? 0), t)),
      outflow: String(lerp(Number(na?.outflow ?? 0), Number(nb?.outflow ?? 0), t)),
      volume: String(lerp(Number(na?.volume ?? 0), Number(nb?.volume ?? 0), t)),
    };
  });

  const key = (e: FlowEdge) => `${e.source}->${e.target}`;
  const edgeA = new Map(a.edges.map((e) => [key(e), e]));
  const edgeB = new Map(b.edges.map((e) => [key(e), e]));
  const edgeKeys = new Set([...edgeA.keys(), ...edgeB.keys()]);

  const edges = [...edgeKeys].map((k) => {
    const ea = edgeA.get(k);
    const eb = edgeB.get(k);
    const base = ea ?? eb!;
    return {
      ...base,
      value: String(lerp(Number(ea?.value ?? 0), Number(eb?.value ?? 0), t)),
      count: Math.round(lerp(ea?.count ?? 0, eb?.count ?? 0, t)),
    };
  // An edge blended to zero contributes nothing and would still cost a draw
  // call plus a particle, so drop it.
  }).filter((e) => Number(e.value) > 0.005);

  return {
    // Identity stays with the nearer period so labels and drill-down targets
    // remain real months, never a fabricated date.
    period: t < 0.5 ? a.period : b.period,
    label: t < 0.5 ? a.label : b.label,
    nodes,
    edges,
    total_in: String(lerp(Number(a.total_in), Number(b.total_in), t)),
    total_out: String(lerp(Number(a.total_out), Number(b.total_out), t)),
  };
}

/**
 * Resolve a fractional timeline position into a renderable frame.
 *
 * Args:
 *   periods: All periods, chronological.
 *   pos: Fractional index; 2.4 means "40% of the way from period 2 to 3".
 *
 * Returns:
 *   FlowPeriod: The blended frame, or the nearest real period at the bounds.
 */
export function frameAt(periods: FlowPeriod[], pos: number): FlowPeriod {
  if (!periods.length) throw new Error('frameAt requires at least one period');
  const clamped = Math.max(0, Math.min(periods.length - 1, pos));
  const i = Math.floor(clamped);
  const f = clamped - i;
  const a = periods[i];
  const b = periods[Math.min(periods.length - 1, i + 1)];
  return interpolatePeriods(a, b, f);
}
